import React, { forwardRef } from "react";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { Input } from "./input";
import { cn } from "@/lib/utils";

// Custom styled input component
const CustomInput = forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement>
>(({ className, ...props }, ref) => (
  <Input className={cn("pl-14", className)} {...props} ref={ref} />
));
CustomInput.displayName = "CustomInput";

interface PhoneInputShadcnProps {
  value: string;
  onChange: (value: string | undefined) => void;
  className?: string;
  defaultCountry?: string;
  [key: string]: any;
}

export function PhoneInputShadcn({
  value,
  onChange,
  className,
  defaultCountry = "CA",
  ...props
}: PhoneInputShadcnProps) {
  // Add global styles once when component is used
  React.useEffect(() => {
    // Create style element if it doesn't exist
    const styleId = "phone-input-shadcn-styles";
    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.innerHTML = `
        .PhoneInput .PhoneInputCountry {
          position: absolute;
          height: 100%;
          display: flex;
          align-items: center;
          z-index: 10;
          left: 0.75rem;
        }
        
        .PhoneInput .PhoneInputCountryIcon {
          width: 1.5rem;
          height: 1.13rem;
          border-radius: 0.125rem;
          overflow: hidden;
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  // Custom onChange handler to validate and only allow Canadian numbers
  const handleChange = (newValue: string | undefined) => {
    // Allow empty value for clearing the input
    if (!newValue) {
      onChange(undefined);
      return;
    }

    // Check if this is a valid Canadian phone number (country code +1 and valid format)
    const isCanadian =
      newValue.startsWith("+1") && isValidPhoneNumber(newValue, "CA");

    // Only update if it's a Canadian number or empty
    if (isCanadian) {
      onChange(newValue);
    }
  };

  return (
    <div className={cn("relative", className)}>
      <PhoneInput
        international
        value={value}
        onChange={handleChange}
        inputComponent={CustomInput}
        countrySelectProps={{
          className: "absolute inset-0 opacity-0 cursor-pointer",
        }}
        countries={["CA"]} // Only allow Canada
        defaultCountry="CA" // Always default to Canada
        {...props}
      />
    </div>
  );
}
